import React, { useEffect, useRef, useState } from "react";

const IconBuilding = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18" />
    <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" />
    <path d="M9 7h2M9 11h2M9 15h2M13 7h2M13 11h2M13 15h2" />
  </svg>
);

const IconGavel = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 12l-8 8" />
    <path d="M15 5l4 4" />
    <path d="M9 11l4 4" />
    <path d="M9 7l4 4" />
    <path d="M3 21h8" />
    <path d="M12 6l3-3 6 6-3 3z" />
  </svg>
);

const IconShield = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l8 4v6c0 5-3.3 9.4-8 10-4.7-.6-8-5-8-10V6l8-4z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const IconInfo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z" />
    <path d="M12 10v7" />
    <path d="M12 7h.01" />
  </svg>
);

const IconRisk = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l10 18H2L12 2z" />
    <path d="M12 9v5" />
    <path d="M12 17h.01" />
  </svg>
);

const IconLaw = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 21h8" />
    <path d="M10 21V7a2 2 0 1 1 4 0v14" />
    <path d="M6 5h12" />
    <path d="M6 5l2 4h8l2-4" />
  </svg>
);

const IconMonitor = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 5h16v10H4z" />
    <path d="M8 19h8" />
    <path d="M12 15v4" />
    <path d="M7 12l2-2 2 2 4-4 2 2" />
  </svg>
);

const IconChart = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19h16" />
    <path d="M7 16V8" />
    <path d="M12 16V5" />
    <path d="M17 16v-6" />
  </svg>
);

const Donut = ({ size = 150, stroke = 18, segments, showLabels = false }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;
  let dashAcc = 0;
  let angleAcc = 0;
  const arcs = segments.map((s) => {
    const dash = (s.value / 100) * c;
    const arcAngle = (s.value / 100) * 2 * Math.PI;
    const midAngle = angleAcc + arcAngle / 2;
    const item = { s, dash, dashOffset: dashAcc,
      lx: cx + r * Math.sin(midAngle),
      ly: cy - r * Math.cos(midAngle),
    };
    dashAcc += dash;
    angleAcc += arcAngle;
    return item;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`translate(${cx} ${cy}) rotate(-90)`}>
        <circle r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        {arcs.map(({ s, dash, dashOffset }) => (
          <circle key={s.label} r={r} fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-dashOffset} strokeLinecap="butt" />
        ))}
      </g>
      {showLabels && arcs.map(({ s, lx, ly }) => (
        <text key={s.label + "-lbl"} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
          fontSize={Math.round(stroke * 0.42)} fontWeight="900" fill="white">
          {s.value}%
        </text>
      ))}
    </svg>
  );
};

const BarMini = ({ data }) => {
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div className="flex w-full items-end justify-center gap-6 pt-8">
      {data.map((d) => (
        <div key={d.label} className="flex flex-col items-center gap-2 flex-1">
          <div className="relative h-40 w-full">
            <div className="absolute bottom-0 w-full rounded-lg"
              style={{ height: `${(d.value / max) * 100}%`, background: d.color }}>
              <div className="absolute -top-8 w-full text-center text-[18px] font-black text-slate-900">
                {d.value}%
              </div>
            </div>
          </div>
          <div className="text-[13px] font-bold text-slate-700 text-center leading-snug">
            {d.label}
            {d.sub && <div className="text-[12px] font-semibold text-slate-400">{d.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const StatItem = ({ label, value, note, icon: Icon }) => (
  <div className="flex flex-col items-center text-center gap-4">
    <div className="h-16 w-16 shrink-0 rounded-full border border-cyan-400/60 bg-cyan-400/10 flex items-center justify-center text-cyan-200">
      {Icon ? <Icon className="h-8 w-8" /> : null}
    </div>
    <div>
      <div className="text-[14px] font-bold text-white/70 tracking-wide">{label}</div>
      <div className="mt-1 text-[48px] sm:text-[56px] font-black text-white leading-none">{value}</div>
      {note ? <div className="mt-2 text-[12px] font-bold text-white/50">{note}</div> : null}
    </div>
  </div>
);

const useSequentialCards = (count) => {
  const ref = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        Array.from({ length: count }).forEach((_, i) => {
          setTimeout(() => setVisibleCount(i + 1), i * 350);
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [count]);
  return { ref, visibleCount };
};

const AnimatedCard = ({ children, index, visibleCount, className = "" }) => {
  const visible = visibleCount > index;
  return (
    <div
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(48px) scale(0.97)",
        transition: "opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      {children}
    </div>
  );
};

const NoteBar = ({ children }) => (
  <div className="mt-6 rounded-xl bg-blue-50 px-4 py-3 text-[14px] lg:text-[17px] font-bold text-blue-900">
    <div className="leading-snug">{children}</div>
  </div>
);

const SeriousAccidentInfographic = ({ bgImageSrc = "/img/serious-accident-banner.png" }) => {
  const { ref: cardsRef, visibleCount: cardsVisible } = useSequentialCards(3);
  const { ref: ctaRef, visibleCount: ctaVisible } = useSequentialCards(1);

  const donut1 = [
    { label: "A", value: 40, color: "#2563EB" },
    { label: "B", value: 25, color: "#14B8A6" },
    { label: "C", value: 20, color: "#7C3AED" },
    { label: "D", value: 15, color: "#F97316" },
  ];

  const donut2 = [
    { label: "A", value: 44.2, color: "#2563EB" },
    { label: "B", value: 32.2, color: "#14B8A6" },
    { label: "C", value: 23.6, color: "#6B7280" },
  ];

  const bars = [
    { label: "전문 인력 부족", sub: null, value: 32.8, color: "#2563EB" },
    { label: "모호한 법 기준", sub: "(의무 범위 불명확)", value: 28.7, color: "#14B8A6" },
    { label: "과도한 처벌 리스크", sub: "(1년 이상 징역)", value: 24.1, color: "#7C3AED" },
  ];

  return (
    // Override global `section { padding/border/radius }` from legacy CSS so the hero can be full-bleed.
    <section className="w-full !m-0 !p-0 !border-0 !rounded-none bg-transparent">
      {/* top dark hero */}
      {/* Quote 섹션처럼: 부모 안에서 w-full 배경 꽉 차게 */}
      <div className="relative w-full overflow-hidden bg-slate-950">
        <div
          className="absolute inset-0 bg-center bg-cover opacity-55"
          style={bgImageSrc ? { backgroundImage: `url(${bgImageSrc})` } : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/10" />

        <div className="relative px-6 py-14 sm:px-10 sm:py-16 md:px-14">
          <p className="text-[40px] sm:text-[54px] md:text-[66px] font-black text-white leading-[1.25]">
            안전은 선택이 아닌,
            <br />
            기업의 <span className="text-[#1E7BFF]">핵심 의무</span>입니다
          </p>
          <h2 className="mt-4 text-[16px] sm:text-[20px] md:text-[24px] font-extrabold text-white leading-snug">
            중대재해처벌법에 따른 경영책임자의 안전보건 확보 의무를 통계로 확인하세요.
          </h2>

          <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
            <div className="sm:pr-10">
              <StatItem label="적용 사업장 수" value="83만+" note="* 50인 이상 사업장 기준" icon={IconBuilding} />
            </div>
            <div className="sm:px-10">
              <StatItem label="의무 위반 시 형사처벌" value="1년 이상 징역" note="* 중대재해처벌법 제6조" icon={IconGavel} />
            </div>
            <div className="sm:pl-10">
              <StatItem label="안전보건 확보 의무" value="4대 핵심 의무" note="* 경영책임자 필수 이행 사항" icon={IconShield} />
            </div>
          </div>
        </div>
      </div>

      {/* bottom cards */}
      <div className="bg-slate-50 px-3 py-10 sm:px-4 md:px-6 lg:px-8">
        <div ref={cardsRef} className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-stretch">
          {/* 카드 1 */}
          <AnimatedCard index={0} visibleCount={cardsVisible} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default">
            <div className="text-[40px] lg:text-[55px] font-black text-blue-600 leading-none">01</div>
            <div className="mt-1 text-[18px] lg:text-[25px] font-black text-slate-900 leading-snug">
              경영책임자의 핵심
              <span className="text-blue-600"> ‘안전보건확보 의무’</span>
              <span> (법적 기준)</span>
            </div>
            <p className="mt-3 text-[14px] lg:text-[16px] font-semibold text-slate-500 leading-relaxed">
              중대재해처벌법의 핵심은 경영책임자(CEO 등)에게 직접적인 예방 의무를 부여하고, 위반 시 형사처벌을 한다는 점입니다.
            </p>

            <div className="mt-5 space-y-4 flex-1">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full bg-blue-600" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[18px] lg:text-[20px] font-bold text-blue-600">안전보건관리체계의 구축 및 이행</span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[12px] font-extrabold text-blue-600">가장 핵심적인 의무</span>
                  </div>
                  <div className="mt-0.5 text-[16px] lg:text-[18px] font-semibold text-slate-400">재해 예방에 필요한 인력, 시설, 장비 구비 및 예산 편성</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full bg-teal-500" />
                <div>
                  <div className="text-[18px] lg:text-[20px] font-bold text-teal-600">재해 발생 시 재발방지 대책 수립 및 이행</div>
                  <div className="mt-0.5 text-[16px] lg:text-[18px] font-semibold text-slate-400">사고 발생 후 명확한 대책 마련 여부</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full bg-violet-600" />
                <div>
                  <div className="text-[18px] lg:text-[20px] font-bold text-violet-600">중앙행정기관 등의 시정명령 이행</div>
                  <div className="mt-0.5 text-[16px] lg:text-[18px] font-semibold text-slate-400">정부의 개선 지시 사항 준수</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full bg-orange-500" />
                <div>
                  <div className="text-[18px] lg:text-[20px] font-bold text-orange-500">안전·보건 관계 법령상 의무이행에 필요한 관리상의 조치</div>
                  <div className="mt-0.5 text-[16px] lg:text-[18px] font-semibold text-slate-400">산업안전보건법 등 관련 법령이 현장에서 잘 지켜지도록 관리·감독할 의무</div>
                </div>
              </div>
            </div>

            <NoteBar>핵심은 ‘사전 예방을 위한 안전보건관리체계 구축 및 이행’입니다.</NoteBar>
          </AnimatedCard>

          {/* 카드 2 */}
          <AnimatedCard index={1} visibleCount={cardsVisible} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default">
            <div className="text-[40px] lg:text-[55px] font-black text-violet-600 leading-none">02</div>
            <div className="mt-1 text-[18px] lg:text-[25px] font-black text-slate-900 leading-snug">
              현장의 목소리:
              <span className="text-violet-600"> "왜 법을 지키기 어려운가?"</span>
            </div>
            <p className="mt-3 text-[14px] lg:text-[16px] font-semibold text-slate-500 leading-relaxed">
              기업들이 이 법을 두려워하고 어려워하는 명확한 이유를 경제 단체의 설문조사 통계로 확인하세요.
            </p>

            <div className="mt-5 space-y-4 flex-1">
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5">
                <div className="text-[14px] lg:text-[16px] font-extrabold text-blue-600 tracking-widest">가장 큰 부담</div>
                <div className="mt-1 flex items-end gap-2">
                  <span className="text-[38px] lg:text-[47px] font-black text-blue-700 leading-none">32.8%</span>
                  <span className="mb-1 text-[15px] lg:text-[18px] font-bold text-slate-500">의 중소기업</span>
                </div>
                <p className="mt-2 text-[14px] lg:text-[16px] font-semibold text-slate-700 leading-relaxed">
                  ‘안전보건 전문인력 배치’를 가장 큰 부담(1+2순위 종합)으로 꼽았습니다.
                </p>
                <div className="mt-2 text-[12px] lg:text-[14px] font-semibold text-slate-400 leading-relaxed">
                  출처: 중소기업중앙회(KBIZ), 『중대재해처벌법 시행 관련 중소기업 애로조사』(2023)
                </div>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-5">
                <div className="text-[14px] lg:text-[16px] font-extrabold text-violet-600">’모호한 법 기준’과 ‘과도한 처벌’의 공포</div>
                <p className="mt-2 text-[14px] lg:text-[16px] font-semibold text-slate-700 leading-relaxed">
                  ‘불명확한 경영책임자의 범위 및 의무 내용’과 ‘과도한 형사처벌(1년 이상 징역)’이 현장의 가장 큰 고충입니다.
                </p>
                <p className="mt-1 text-[13px] lg:text-[15px] font-semibold text-slate-500 leading-relaxed">
                  무엇을 어디까지 준비해야 합법인지 판단하기 어렵다는 것이 기업들의 핵심 어려움입니다.
                </p>
                <div className="mt-2 text-[12px] lg:text-[14px] font-semibold text-slate-400 leading-relaxed">
                  출처: 한국경영자총협회(KEF), 『중대재해처벌법 시행 관련 기업 설문조사』
                </div>
              </div>
            </div>

            <NoteBar>가장 큰 부담은 ‘전문 인력 부족’과 ‘모호한 법 기준’입니다.</NoteBar>
          </AnimatedCard>

          {/* 카드 3 */}
          <AnimatedCard index={2} visibleCount={cardsVisible} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default">
            <div className="text-[40px] lg:text-[55px] font-black text-teal-500 leading-none">03</div>
            <div className="mt-1 text-[18px] lg:text-[25px] font-black text-slate-900 leading-snug">
              중소기업의 딜레마:
              <span className="text-teal-500"> 사고는 어디서 발생하는가?</span>
            </div>
            <p className="mt-3 text-[14px] lg:text-[16px] font-semibold text-slate-500 leading-relaxed">
              인력과 자본이 부족한 중소규모 사업장이 법 준수에 가장 큰 어려움을 겪지만, 역설적으로 가장 많은 사고가 발생하는 곳도 바로 소규모 사업장입니다.
            </p>

            <div className="mt-5 flex-1 space-y-4">
              <div className="rounded-xl bg-teal-50/60 border border-teal-100 p-5 text-center">
                <div className="text-[14px] lg:text-[16px] font-extrabold text-slate-600">전체 사망사고 중</div>
                <div className="mt-1 text-[44px] lg:text-[52px] font-black text-teal-600 leading-none">76.4%</div>
                <div className="mt-1 text-[14px] lg:text-[17px] font-bold text-slate-700">가 50인 미만 사업장에서 발생</div>
              </div>
              <div className="space-y-3">
                {[
                  { color: "bg-blue-600", label: "5~49인 사업장", pct: "44.2%" },
                  { color: "bg-teal-500", label: "4인 미만 사업장", pct: "32.2%" },
                  { color: "bg-slate-400", label: "50인 이상 사업장", pct: "23.6%" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`h-3.5 w-3.5 shrink-0 rounded-sm ${item.color}`} />
                      <span className="text-[15px] lg:text-[18px] font-bold text-slate-700">{item.label}</span>
                    </div>
                    <span className="text-[18px] lg:text-[23px] font-black text-slate-900">{item.pct}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 text-[12px] lg:text-[14px] font-semibold text-slate-400 leading-relaxed">
              출처: 한국산업안전보건공단(KOSHA) 산업재해 현황 통계 (2024)
            </div>

            <NoteBar>76% 이상의 사망사고가 소규모 사업장에서 발생합니다.</NoteBar>
          </AnimatedCard>
        </div>

        {/* bottom CTA strip */}
        <div
          ref={ctaRef}
          className="mt-6 rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm"
          style={{
            opacity: ctaVisible > 0 ? 1 : 0,
            transform: ctaVisible > 0 ? "translateY(0) scale(1)" : "translateY(48px) scale(0.97)",
            transition: "opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_1.4fr] lg:items-center">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 shrink-0 rounded-2xl bg-blue-600/10 text-blue-700 flex items-center justify-center">
                <IconShield className="h-7 w-7" />
              </div>
              <div>
                <div className="text-[21px] sm:text-[25px] font-black text-slate-900 leading-tight">
                  안전관리는 사람의 노력이 아닌, 시스템의 역할입니다.
                </div>
                <div className="mt-2 text-[16px] sm:text-[18px] font-bold text-slate-500 whitespace-nowrap">
                  ATOS 중대재해대시보드와 함께 중대재해 없는 안전한 사업장을 만들어가세요.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 px-3 py-4 text-center">
                <IconMonitor className="h-2 w-2 text-blue-700" />
                <div>
                  <div className="text-[20px] font-extrabold text-slate-700">대시보드</div>
                  <div className="text-[18px] font-semibold text-slate-400">현황 확인</div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 px-3 py-4 text-center">
                <IconRisk className="h-2 w-2 text-blue-700" />
                <div>
                  <div className="text-[20px] font-extrabold text-slate-700">재해관리</div>
                  <div className="text-[18px] font-semibold text-slate-400">공정·설비 관리</div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 px-3 py-4 text-center">
                <IconBuilding className="h-2 w-2 text-blue-700" />
                <div>
                  <div className="text-[20px] font-extrabold text-slate-700">사업장관리</div>
                  <div className="text-[18px] font-semibold text-slate-400">조직 정보 관리</div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 px-3 py-4 text-center">
                <IconChart className="h-2 w-2 text-blue-700" />
                <div>
                  <div className="text-[20px] font-extrabold text-slate-700">AI 서비스</div>
                  <div className="text-[18px] font-semibold text-slate-400">AI 안전 문의</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeriousAccidentInfographic;
